import {
  type DragEndEvent,
  type DragStartEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import {
  Sortable,
  SortableContent,
  SortableOverlay,
} from "./components/ui/sortable";
import {
  type ReorderableColumn,
  getColumnPinsAfterReorder,
  getReorderableColumnKeys,
  isColumnReorderable,
  isColumnReorderingEnabled,
  reorderDisplayedColumns,
} from "./table2-column-reorder";

const DRAG_ACTIVATION_DISTANCE = 8;
const horizontalDragModifiers = [restrictToHorizontalAxis];

export interface UseTable2ColumnReorderingArgs<
  Column extends ReorderableColumn,
  DisplayedColumn extends ReorderableColumn,
> {
  columns: Column[];
  columnPins: Record<string, boolean>;
  displayedColumns: DisplayedColumn[];
  enableColumnReordering?: boolean;
  setColumnPins(columnPins: Record<string, boolean>): void;
  setDisplayedColumns(columns: DisplayedColumn[]): void;
}

export function useTable2ColumnReordering<
  Column extends ReorderableColumn,
  DisplayedColumn extends ReorderableColumn,
>({
  columns,
  columnPins,
  displayedColumns,
  enableColumnReordering,
  setColumnPins,
  setDisplayedColumns,
}: UseTable2ColumnReorderingArgs<Column, DisplayedColumn>) {
  const [activeColumnKey, setActiveColumnKey] = useState<string | null>(null);

  const dndSensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE },
    }),
  );

  const reorderableColumns = useMemo<ReorderableColumn[]>(
    () => columns.filter(isColumnReorderable),
    [columns],
  );
  const enabled = isColumnReorderingEnabled({
    enableColumnReordering,
    reorderableColumnKeys: getReorderableColumnKeys(columns),
  });

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveColumnKey(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(() => {
    setActiveColumnKey(null);
  }, []);

  const handleMove = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }

      const activeKey = String(active.id);
      const overKey = String(over.id);
      setDisplayedColumns(
        reorderDisplayedColumns({ activeKey, displayedColumns, overKey }),
      );
      setColumnPins(
        getColumnPinsAfterReorder({ activeKey, columnPins, overKey }),
      );
    },
    [columnPins, displayedColumns, setColumnPins, setDisplayedColumns],
  );

  const renderColumnReorderContext = useCallback(
    (table: ReactNode, dragOverlay?: ReactNode) => {
      if (!enabled) {
        return table;
      }

      return (
        <Sortable
          collisionDetection={closestCenter}
          getItemValue={(column) => column.key}
          modifiers={horizontalDragModifiers}
          onDragCancel={handleDragEnd}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
          onMove={handleMove}
          orientation="horizontal"
          sensors={dndSensors}
          value={reorderableColumns}
        >
          <SortableContent withoutSlot>{table}</SortableContent>
          <SortableOverlay dropAnimation={null}>{dragOverlay}</SortableOverlay>
        </Sortable>
      );
    },
    [
      dndSensors,
      enabled,
      handleDragEnd,
      handleDragStart,
      handleMove,
      reorderableColumns,
    ],
  );

  return { activeColumnKey, enabled, renderColumnReorderContext };
}
